/**
 * Anomaly Queue sidebar panel (L.2.2, operator-cop-doctrine §3B.1 + §9.4 WebEOC
 * additions). The operator's "what needs my attention right now" list — list-first,
 * bound 1:1 with map markers, ranked by Response Level (impact), NEVER confidence.
 *
 * Sources: AI-fused high-risk hotspots + active Severe/Extreme CAP polygons +
 * smoke_detected anomalies. Rows: two-line, RL badge + 4px left accent bar, count
 * chips, a collapsible sort/filter strip, a new→working→resolving micro-status
 * (rendered New/In review/Cleared — #539), a free-text assignee chip (§9.4), ✓ Ack
 * (demotes to a collapsed Acknowledged section,
 * marker stays — §6.1), and auto-leave on resolve (resolved records simply stop
 * being fetched).
 *
 * Working state (ack + micro-status + owner) is SERVER-PERSISTED via the unified
 * anomaly_workstate table (migration 0058, design-council Part II/VIII → #140): it
 * rides each fetched item, survives a reload, and is visible to the next shift —
 * replacing the old client-side in-memory Set/Map + window.prompt owner path. Writes
 * (PUT /api/anomaly-workstate) apply optimistically to the displayed row.
 *
 * Queue-ergonomics bundle (design-council Part VIII):
 *   • #146 bbox-decouple — the queue is viewport-scoped, so a red outside the map
 *     frame would be invisible. A national count runs alongside the viewport fetch;
 *     a "▲ N outside view — Show all Canada" chip widens the queue to the national
 *     set (the MAP stays put) so the triage surface never silently hides a red.
 *   • #144 bulk ack — per-row select checkboxes + an "Ack selected" bar looping the
 *     persisted ack path (no new verb), for surge triage.
 *   • #145 keyboard — j/k roving row focus, Enter opens, a acks, x selects; the
 *     status/owner/ack chips get real keydown handlers (the a11y defect fix).
 *   • #147 since= — an optional recency lower bound on the fetches + a shift-start
 *     strip with a "new since shift" count and an opt-in "only new" view (pairs
 *     with #143's arrival header).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { WallTourDeskStrip } from './WallTourDeskStrip';
import type { ActiveFireFeature, CapAlertFeature, HighRiskHotspotFeature } from '@tsr/shared-types';
import { escalationSafeResponseLevel, deriveOcFireWatchScore } from '@tsr/shared-types';
import type {
  FloodAnomalyFeature,
  IceStormAnomalyFeature,
  MediaSignalAnomalyFeature,
  SmokeAnomalyFeature,
} from './api';
import {
  CANADA_BBOX,
  fetchActiveFires,
  fetchAnomalyCounts,
  fetchExtremeWeatherWarnings,
  fetchFloodAnomalies,
  fetchHighRiskHotspots,
  fetchIceStormAnomalies,
  fetchMediaSignalAnomalies,
  fetchSmokeDetectionAnomalies,
  putAnomalyWorkstate,
  type BboxQuery,
  type MicroStatus,
} from './api';
import {
  capToQueueItem,
  countUrgentBySource,
  countQueue,
  floodToQueueItem,
  hazardFilterLabel,
  hotspotToQueueItem,
  iceStormToQueueItem,
  isOrphan,
  mediaSignalToQueueItem,
  ocFireToQueueItem,
  outsideViewTotal,
  partitionQueue,
  reconcileQueue,
  smokeToQueueItem,
  sortQueue,
  standingAlarmStatus,
  type QueueHazard,
  type QueueItem,
  type SourceCounts,
} from './anomalyQueue';
import { RESPONSE_LEVEL_COLORS, URGENT_MIN_RESPONSE_LEVEL, isResponseLevel } from './styles/responseLevel';
import { SMOKE_ANOMALY_COLOR } from './smokeAnomalyStyle';
import { MEDIA_SIGNAL_ANOMALY_COLOR, MEDIA_SIGNAL_CHIP_LABEL } from './mediaSignalAnomalyStyle';
import { ACK_STEEL } from './layers/anomalyMarker';
import { ResponseLevelBadge } from './ResponseLevelBadge';
import { MutedChip } from './MutedChip';
import { logAction, getSessionId } from './lib/operatorActions';
import { useShiftStart, clockLabel } from './lib/shiftStart';
import {
  SEAT_NAME_MAX,
  dismissSeatPrompt,
  isSeatPromptDismissed,
  useSeatName,
} from './lib/seatIdentity';
import { broadcastWorkstate, useWorkstateBridge } from './workstateBridge';
import { ALERTS_UNAVAILABLE_EMPTY } from './apiHealthVocabulary';

const REFRESH_DEBOUNCE_MS = 400;
/** Passive row re-poll (#264 cross-desk claim propagation) — same 30 s cadence as
 *  the wall queue + the panel's count-signal poll. Rides the §3B.1 reconcile path,
 *  so a background refresh NEVER reshuffles rows: displayed rows content-refresh
 *  in place (an ack/claim recede surfaces this way), arrivals park behind the
 *  "N new ↑" header, and a pointer-over freeze buffers the whole apply. NB: a
 *  hidden/background Chrome tab throttles timers to ~1/min — a minimized desk
 *  simply catches up on its next tick. */
const QUEUE_POLL_MS = 30_000;
const STATUS_ORDER: MicroStatus[] = ['new', 'working', 'resolving'];
/** #539 — humanize the micro-status enum at the RENDER layer only (ledger §3.C /
 *  reference §2.3). The stored values stay `new`/`working`/`resolving` (§4.2); only
 *  the operator-facing label changes. "Cleared" retires the §2.3 "resolving"
 *  lifecycle-end drift word; casing fixes the raw-enum leak. */
const MICRO_STATUS_LABEL: Record<MicroStatus, string> = {
  new: 'New',
  working: 'In review',
  resolving: 'Cleared',
};
/** Hazard render/keyboard-traversal order (grouped + flat share this order). */
const HAZARD_ORDER: QueueHazard[] = ['fire', 'weather', 'smoke', 'ice_storm', 'flood', 'media_signal'];

type RawById = Map<
  string,
  | { source: 'hotspot'; feature: HighRiskHotspotFeature }
  | { source: 'cap'; feature: CapAlertFeature }
  | { source: 'smoke'; feature: SmokeAnomalyFeature }
  | { source: 'ice_storm'; feature: IceStormAnomalyFeature }
  | { source: 'flood'; feature: FloodAnomalyFeature }
  | { source: 'media_signal'; feature: MediaSignalAnomalyFeature }
  | { source: 'oc_fire'; feature: ActiveFireFeature }
>;

function relTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function accentColor(item: QueueItem): string {
  if (item.source === 'smoke') return SMOKE_ANOMALY_COLOR;
  if (item.source === 'media_signal') return MEDIA_SIGNAL_ANOMALY_COLOR;
  return isResponseLevel(item.responseLevel)
    ? RESPONSE_LEVEL_COLORS[item.responseLevel]
    : RESPONSE_LEVEL_COLORS[1];
}

/** Click + keyboard activation for an inline chip (the #145 a11y fix). Enter/Space
 *  fire the same action as a click; both stop propagation so the row's own
 *  open-on-Enter / select-on-click never double-fires. */
function activate(fn: () => void): {
  onClick: (e: React.MouseEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
} {
  return {
    onClick: (e) => {
      e.stopPropagation();
      fn();
    },
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        fn();
      }
    },
  };
}

export interface AnomalyQueuePanelProps {
  bbox: BboxQuery | null;
  /** Anomaly Queue layer on (urgentAnomalies). When off, the queue is paused. */
  visible: boolean;
  /** CAP-polygon subset visible (the EWW mute) — gates the CAP source. */
  capVisible: boolean;
  /** id of the currently-selected queue item (source:id), to highlight its row. */
  selectedId: string | null;
  /** Bumping this forces a refetch (after a confirm/dismiss resolves an item). */
  refreshNonce: number;
  /** Display names of active class-level mutes (#133) — each renders a "muted"
   *  chip in the header so a shelved class survives a shift handover. */
  mutedClasses?: string[];
  onSelectHotspot: (h: HighRiskHotspotFeature) => void;
  onSelectCap: (c: CapAlertFeature) => void;
  onSelectSmoke: (s: SmokeAnomalyFeature) => void;
  onSelectIce: (i: IceStormAnomalyFeature) => void;
  onSelectFlood: (f: FloodAnomalyFeature) => void;
  onSelectMediaSignal: (m: MediaSignalAnomalyFeature) => void;
  /** #444 — open an urgent OC wildfire (reuses the FireBody side panel). */
  onSelectOcFire: (f: ActiveFireFeature) => void;
  /** Fly the camera to an incident point (reuse R.3 flyToIncident). */
  onFlyTo: (lat: number, lon: number) => void;
  /** #424 — emit the in-view URGENT count (RL≥3 = l3+l4) so the rail "Alerts"
   *  badge equals the visible red cards by construction (single source of truth =
   *  countQueue), instead of a separate map-layer mirror that omitted CAP/flood/ice.
   *  Optional: most call sites (e.g. the operations-panel instance) don't badge.
   *  #325 — emits `null` (UNKNOWN, not a number) while the API is unreachable, so the
   *  rail badge renders "—" not a stale/false "0". Distinguishing UNKNOWN from ZERO
   *  is the whole honest-degradation fix. */
  onCountChange?: (urgentCount: number | null) => void;
  /** #325 — client API reachability (from useApiHealth). While false, the empty-state
   *  says "Alerts unavailable — cannot reach live data" (NOT the false "No urgent
   *  alerts in view" all-clear) and the emitted count goes UNKNOWN. Defaults true so
   *  every existing mount behaves exactly as before. Threads ONLY the honest-degradation
   *  signal — it never touches what the panel fetches. */
  reachable?: boolean;
}

const ARRIVAL_HIGHLIGHT_MS = 8_000;

export function AnomalyQueuePanel(props: AnomalyQueuePanelProps): JSX.Element {
  const {
    bbox,
    visible,
    capVisible,
    selectedId,
    refreshNonce,
    mutedClasses,
    onCountChange,
    reachable = true,
  } = props;
  const [items, setItems] = useState<QueueItem[]>([]);
  // Arrival stability (#143, §3B.1): new rows are parked behind a "N new ↑" header
  // instead of being spliced into the live order — a mid-triage arrival must never
  // reshuffle the row under the cursor. They merge (and re-sort) only on click.
  const [pendingNew, setPendingNew] = useState<QueueItem[]>([]);
  const [highlightIds, setHighlightIds] = useState<Set<string>>(new Set());
  const itemsRef = useRef<QueueItem[]>([]);
  const pendingRef = useRef<QueueItem[]>([]);
  // While the pointer is over the list we freeze ordering entirely: the latest
  // fetch is buffered and applied only once the pointer leaves.
  const interactingRef = useRef(false);
  const bufferedRef = useRef<QueueItem[] | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rawByIdRef = useRef<RawById>(new Map());

  // #146 bbox-decouple: national counts (fetched for CANADA_BBOX alongside the
  // viewport fetch) + the "show all Canada" widen toggle. The map never moves.
  const [showAllCanada, setShowAllCanada] = useState(false);
  const [nationalCounts, setNationalCounts] = useState<SourceCounts | null>(null);

  // #144 bulk ack: per-row selection for "Ack selected".
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // #145 keyboard: roving row focus + the row-element registry to drive .focus().
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // #147 since=: shift-start anchor (shared with the Shift Log #148, persisted so
  // it survives reload within a shift) + an opt-in "only new since shift" view
  // that passes since= to the fetches.
  const { shiftStart, reset: resetShiftStartAnchor } = useShiftStart();
  const [sinceFocus, setSinceFocus] = useState(false);
  // ★ Orphan filter (feat/ownership-coordination) — opt-in narrowing to UN-OWNED hazards at Threat
  // Level ≥3 (the hazard nobody has assumed command of). The PEOC's #1 ask.
  const [orphansOnly, setOrphansOnly] = useState(false);

  // #264 — this desk's seat name (honor-system, pre-RBAC; see lib/seatIdentity).
  // Rides every claim-acknowledge as owner_label: first ack claims, other desks
  // see "ACK — Claimed by [seat]" and the row goes read-only there.
  const [seat, setSeat] = useSeatName();
  const [seatEditorOpen, setSeatEditorOpen] = useState(false);
  const [seatDraft, setSeatDraft] = useState('');
  const [seatPromptDismissed, setSeatPromptDismissed] = useState(() => isSeatPromptDismissed());

  // #264 — passive re-poll tick driving the cross-desk fetch (see QUEUE_POLL_MS).
  const [pollNonce, setPollNonce] = useState(0);
  useEffect(() => {
    if (!visible) return undefined;
    const id = setInterval(() => setPollNonce((n) => n + 1), QUEUE_POLL_MS);
    return () => clearInterval(id);
  }, [visible]);

  const commitItems = (next: QueueItem[]): void => {
    itemsRef.current = next;
    setItems(next);
  };
  const commitPending = (next: QueueItem[]): void => {
    pendingRef.current = next;
    setPendingNew(next);
  };
  /** Drop the displayed + pending sets so the next fetch repaints from scratch.
   *  Used when a mode toggle (Show all Canada / since-focus) changes the universe —
   *  the new rows should appear immediately, not park behind the "N new" header. */
  const resetDisplayed = (): void => {
    commitItems([]);
    commitPending([]);
    bufferedRef.current = null;
  };

  // Reconcile a freshly-fetched (already-sorted) set against what's displayed.
  const applyIncoming = (incoming: QueueItem[]): void => {
    if (itemsRef.current.length === 0 && pendingRef.current.length === 0) {
      // First paint (or after a reset): show the whole set sorted, nothing pending.
      commitItems(incoming);
      return;
    }
    const { items: nextItems, pending } = reconcileQueue(
      itemsRef.current,
      pendingRef.current,
      incoming,
    );
    commitItems(nextItems);
    commitPending(pending);
  };

  // Splice the parked arrivals into the live list, re-sort once, and give them the
  // decaying §3B.1 highlight. This is the only place ordering changes on new data.
  const mergePending = (): void => {
    const arrivals = pendingRef.current;
    if (arrivals.length === 0) return;
    commitItems(sortQueue([...itemsRef.current, ...arrivals]));
    commitPending([]);
    setHighlightIds(new Set(arrivals.map((it) => it.id)));
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightIds(new Set()), ARRIVAL_HIGHLIGHT_MS);
  };
  // Per-item working-state (✓ Ack, micro-status, owner) is now SERVER-PERSISTED
  // (migration 0058, #140): it rides each QueueItem from the fetch and survives a
  // reload / shift handover. Writes go to PUT /api/anomaly-workstate and are applied
  // optimistically to the displayed item so the row reacts instantly.
  const [ownerEditId, setOwnerEditId] = useState<string | null>(null);
  const [ownerDraft, setOwnerDraft] = useState('');
  // Controls.
  const [stripOpen, setStripOpen] = useState(false);
  const [hazardFilter, setHazardFilter] = useState<QueueHazard | 'all'>('all');
  const [groupByHazard, setGroupByHazard] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  // #394 de-flood — the "Fusion candidates" drawer (sub-urgent RL1/RL2) is collapsed
  // by default so the urgent bar reads genuine; one click reveals the demoted mass.
  const [candidatesOpen, setCandidatesOpen] = useState(false);

  // The effective query universe: "Show all Canada" widens the bbox to the national
  // envelope (map stays put); "only new since shift" attaches the since= lower bound.
  const effectiveBbox = showAllCanada ? CANADA_BBOX : bbox;
  const sinceParam = sinceFocus ? new Date(shiftStart).toISOString() : undefined;

  // Fetch the five sources on bbox / visibility / mode change.
  useEffect(() => {
    if (!visible || !effectiveBbox) {
      commitItems([]);
      commitPending([]);
      bufferedRef.current = null;
      return undefined;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const [hotspots, smoke, ice, flood, media, cap, ocFires] = await Promise.all([
            // #580 — the queue opts into the server-side Canada scope (canadaScope) so
            // its rows + counts are Canada-scoped/US-clipped like the map, and the
            // OC-fire freshness gate (freshOnly) so an implausibly-stale fixture fire
            // never rides the urgent queue or the "oldest unacknowledged" metric.
            fetchHighRiskHotspots(effectiveBbox, ctl.signal, {
              minConfidence: 60,
              since: sinceParam,
              canadaScope: true,
            }),
            fetchSmokeDetectionAnomalies(effectiveBbox, ctl.signal, sinceParam),
            fetchIceStormAnomalies(effectiveBbox, ctl.signal, sinceParam),
            fetchFloodAnomalies(effectiveBbox, ctl.signal, sinceParam),
            fetchMediaSignalAnomalies(effectiveBbox, ctl.signal, sinceParam),
            capVisible
              ? fetchExtremeWeatherWarnings(effectiveBbox, ctl.signal, sinceParam, { canadaScope: true })
              : Promise.resolve({ type: 'FeatureCollection' as const, features: [] }),
            // #444 source 2 — OC wildfires. The server derives the anti-flood RL; we
            // surface ONLY the urgent band (RL≥3) as queue items (the muted RL2 floor
            // stays a map glyph). Independent of capVisible (not an EWW class).
            fetchActiveFires(effectiveBbox, ctl.signal, {
              stages: ['OC'],
              canadaScope: true,
              freshOnly: true,
            }),
          ]);
          if (ctl.signal.aborted) return;
          const raw: RawById = new Map();
          const list: QueueItem[] = [];
          for (const f of hotspots.features) {
            const it = hotspotToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'hotspot', feature: f });
          }
          for (const f of cap.features) {
            const it = capToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'cap', feature: f });
          }
          for (const f of smoke.features) {
            const it = smokeToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'smoke', feature: f });
          }
          for (const f of ice.features) {
            const it = iceStormToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'ice_storm', feature: f });
          }
          for (const f of flood.features) {
            const it = floodToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'flood', feature: f });
          }
          for (const f of media.features) {
            const it = mediaSignalToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'media_signal', feature: f });
          }
          for (const f of ocFires.features) {
            // De-flood gate: only RL≥3 OC fires reach the URGENT surface (RL2 = muted glyph).
            // #445-ws2 — the owning IC's operator pin layers over the derived OC ladder via the
            // ESCALATION-SAFE max, so an escalation lifts an OC RL2 onto the queue while a
            // de-escalation can never bury a worse derived. A non-OC fire (derived null) is
            // never an OC-fire alert here regardless of any incident pin (that surfaces on the
            // incident detail + SITREP instead). The gate reads the effective urgency, not the
            // raw ladder — kept in lockstep with ocFireToQueueItem's responseLevel.
            const derived = f.properties.oc_fire_response_level ?? null;
            if (derived == null) continue;
            const eff =
              escalationSafeResponseLevel(f.properties.operator_response_level ?? null, derived) ?? derived;
            // #618 Defect 2 (fail-loud): a fire whose ONLY claim to urgency was a now-STALE
            // escalation ≥60 must not silently vanish once the ladder gates that escalator off.
            // Keep it on the queue as a `degraded` item (it lands in the candidates drawer, marked
            // "watching degraded" — see ocFireToQueueItem) instead of dropping it. Rare by design
            // (only when scoring stops), so it never floods the urgent bar; the map disc stays
            // honestly muted (derived RL2) and the national count stays reds-only.
            const degraded =
              deriveOcFireWatchScore({
                ocFireResponseLevel: derived,
                escalationIndex: f.properties.escalation_index ?? null,
                escalationUpdatedAt: f.properties.escalation_updated_at ?? null,
                now: Date.now(),
              })?.kind === 'degraded';
            if (eff < URGENT_MIN_RESPONSE_LEVEL && !degraded) continue;
            const it = ocFireToQueueItem(f);
            list.push(it);
            raw.set(it.id, { source: 'oc_fire', feature: f });
          }
          rawByIdRef.current = raw;
          // #447 Track A Slice 3b — the #444 client-side co-located fold is RETIRED: a
          // hotspot that is a known fire's directional downwind spread is already demoted
          // off urgent AT SOURCE (fusion), so there is no pair to fold here.
          const incoming = sortQueue(list);
          // Freeze while the operator is interacting: buffer, apply on pointer-out.
          if (interactingRef.current) {
            bufferedRef.current = incoming;
          } else {
            applyIncoming(incoming);
          }
        } catch (err) {
          if ((err as { name?: string }).name !== 'AbortError') {
            console.error('AnomalyQueue fetch failed', err);
          }
        }
      })();
    }, REFRESH_DEBOUNCE_MS);
    return () => {
      ctl.abort();
      clearTimeout(timer);
    };
    // applyIncoming/commit* only touch refs + stable setters — refetch is driven
    // solely by bbox/visibility/cap/nonce + the mode toggles + the #264 poll tick,
    // never by those helper identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox, visible, capVisible, refreshNonce, pollNonce, showAllCanada, sinceFocus, shiftStart]);

  // #146 — national counts for the "outside view" chip. Skipped when the queue is
  // already national (Show all Canada) since there is then nothing "outside". Uses
  // the same since= as the queue so national − in-view is apples-to-apples.
  useEffect(() => {
    if (!visible || !bbox || showAllCanada) {
      setNationalCounts(null);
      return undefined;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      void fetchAnomalyCounts(CANADA_BBOX, ctl.signal, {
        minConfidence: 60,
        since: sinceParam,
        // #580 — Canada-scope + freshness the national count so national − in-view stays
        // apples-to-apples with the (now Canada-scoped, fresh) in-view queue set.
        canadaScope: true,
        freshOnly: true,
      })
        .then((c) => {
          if (!ctl.signal.aborted) {
            setNationalCounts({
              hotspot: c.hotspot,
              cap: c.cap,
              smoke: c.smoke,
              ice_storm: c.ice_storm,
              flood: c.flood,
              media_signal: c.media_signal,
              oc_fire: c.oc_fire,
            });
          }
        })
        .catch((err) => {
          if ((err as { name?: string }).name !== 'AbortError') {
            console.error('AnomalyQueue national counts failed', err);
          }
        });
    }, REFRESH_DEBOUNCE_MS);
    return () => {
      ctl.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox, visible, capVisible, refreshNonce, pollNonce, showAllCanada, sinceFocus, shiftStart]);

  // Clear the decaying-highlight timer on unmount.
  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    },
    [],
  );

  const counts = useMemo(() => countQueue(items), [items]);
  // #424 — the rail "Alerts" badge = the in-view URGENT reds (RL≥3 = l3+l4) the
  // queue actually shows, emitted from here so badge == visible red cards == the
  // L3/L4 chips by construction (one source of truth: countQueue over these items).
  // Excludes L1/L2 candidates (#370 demoted) and the unscored smoke/media registers,
  // matching the standing-alarm urgency definition. NOTE the badge follows this
  // panel's own view: with "Show all Canada" / "only new since shift" active it
  // tracks that same widened/filtered card set (badge == what's listed, always).
  const urgentCount = counts.l3 + counts.l4;
  // #325 — while the API is unreachable the count is UNKNOWN, not zero: emit `null` so
  // the rail badge renders "—" instead of a stale/false "0" all-clear. The fetches are
  // untouched (they simply fail + keep last-known rows); only the SIGNAL is gated.
  const emittedCount = reachable ? urgentCount : null;
  useEffect(() => {
    onCountChange?.(emittedCount);
  }, [emittedCount, onCountChange]);
  // Standing-alarm ceiling (#129, design-council Part II rule 5): surfaces a triage
  // strip when unacked reds exceed the EEMUA console ceiling or the oldest stands
  // longer than a shift. Recomputed each time the (server-persisted) items change.
  const standingAlarm = useMemo(() => standingAlarmStatus(items, Date.now()), [items]);
  // ★ Orphan count — un-owned hazards at Threat Level ≥3 (a non-exclusive subset of the L3/L4
  // reds, so it is a SEPARATE tally, not a countQueue bucket). Counted over ALL items (parallels
  // the L-chips), independent of the acked/unacked split.
  const orphanCount = useMemo(() => items.filter(isOrphan).length, [items]);

  // #146 — how many reds sit outside the current map frame (national − in view).
  const outsideCount = useMemo(() => {
    if (!nationalCounts) return 0;
    // #383 part-1 — gate the in-view hotspot count to RL>=3 reds to match the now
    // RL>=3-gated national count (anomaly-counts), so national − in-view stays a
    // reds−reds difference (the queue list itself still carries L1/L2 candidates).
    return outsideViewTotal(nationalCounts, countUrgentBySource(items), capVisible);
  }, [nationalCounts, items, capVisible]);

  const select = (item: QueueItem): void => {
    const raw = rawByIdRef.current.get(item.id);
    if (!raw) return;
    if (raw.source === 'hotspot') props.onSelectHotspot(raw.feature);
    else if (raw.source === 'cap') props.onSelectCap(raw.feature);
    else if (raw.source === 'smoke') props.onSelectSmoke(raw.feature);
    else if (raw.source === 'ice_storm') props.onSelectIce(raw.feature);
    else if (raw.source === 'flood') props.onSelectFlood(raw.feature);
    else if (raw.source === 'media_signal') props.onSelectMediaSignal(raw.feature);
    else props.onSelectOcFire(raw.feature);
    // CAP areas are province-scale — no incident zoom; the others fly in.
    if (raw.source !== 'cap') props.onFlyTo(item.lat, item.lon);
  };

  // Optimistically patch a displayed/pending item by id (server write follows).
  const patchItem = (id: string, patch: Partial<QueueItem>): void => {
    const apply = (arr: QueueItem[]): QueueItem[] =>
      arr.map((it) => (it.id === id ? { ...it, ...patch } : it));
    commitItems(apply(itemsRef.current));
    commitPending(apply(pendingRef.current));
  };

  // Patch by (source, sourceId) — the workstate bridge's key (#264), since the
  // QueueItem id prefix ('ice:'/'media:') doesn't match the source_type strings.
  const patchBySource = (source: QueueItem['source'], sourceId: string, patch: Partial<QueueItem>): void => {
    const apply = (arr: QueueItem[]): QueueItem[] =>
      arr.map((it) => (it.source === source && it.sourceId === sourceId ? { ...it, ...patch } : it));
    commitItems(apply(itemsRef.current));
    commitPending(apply(pendingRef.current));
  };

  // #264 — same-machine windows (COP rail + /panel) reflect a sibling's persisted
  // claim/ack/status/owner instantly; cross-desk rides the QUEUE_POLL_MS re-poll.
  useWorkstateBridge((msg) => {
    patchBySource(msg.sourceType, msg.sourceId, {
      acknowledged: msg.work_acknowledged_at != null,
      microStatus: msg.micro_status,
      owner: msg.owner_label,
    });
  });

  // #264 — claimed by another seat: ack'd with a different desk's name on it.
  // Such a row is READ-ONLY here (status/owner controls disabled, owner shown);
  // it stays openable. Honor-system — the comparison is the local seat string.
  const isOwnedByOther = (item: QueueItem): boolean =>
    item.acknowledged && item.owner != null && item.owner !== seat;

  // The persisted ack path (#140), shared by the per-row ✓ Ack, the 'a' keyboard
  // shortcut, and #144 bulk "Ack selected". With a seat name set (#264) the ack
  // is a CLAIM: first-acknowledge-wins server-side; a lost race reconciles to the
  // winning owner below (graceful no-op — the row simply shows who has it).
  const ackItem = (item: QueueItem): void => {
    const claiming = seat != null;
    patchItem(item.id, { acknowledged: true, ...(claiming ? { owner: seat } : {}) });
    void putAnomalyWorkstate(
      item.source,
      item.sourceId,
      claiming ? { acknowledged: true, owner_label: seat, claim: true } : { acknowledged: true },
      getSessionId(),
    )
      .then((res) => {
        // One audit verb per ack, stamped with the claim outcome (claim-vs-plain
        // stays queryable from the payload — no new verb; see tracker note).
        logAction('acknowledge_anomaly', {
          item_id: item.id,
          source: item.source,
          ...(claiming ? { claim: true, claimed: res.claimed !== false, seat } : {}),
        });
        // Server truth — on a lost claim this flips the row to the winning owner.
        patchItem(item.id, {
          acknowledged: res.work_acknowledged_at != null,
          owner: res.owner_label,
          microStatus: res.micro_status,
        });
        broadcastWorkstate(res);
      })
      .catch((err) => {
        logAction('acknowledge_anomaly', {
          item_id: item.id,
          source: item.source,
          ...(claiming ? { claim: true, seat } : {}),
          persist_failed: true,
        });
        console.error('ack persist failed', err);
      });
  };

  const cycleStatus = (item: QueueItem): void => {
    if (isOwnedByOther(item)) return; // #264 read-only on a claimed row
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(item.microStatus) + 1) % STATUS_ORDER.length]!;
    patchItem(item.id, { microStatus: next });
    logAction('set_anomaly_status', { item_id: item.id, status: next });
    void putAnomalyWorkstate(item.source, item.sourceId, { micro_status: next }, getSessionId())
      .then(broadcastWorkstate)
      .catch((err) => console.error('status persist failed', err));
  };

  const openOwnerEditor = (item: QueueItem): void => {
    if (isOwnedByOther(item)) return; // #264 read-only on a claimed row
    setOwnerEditId(item.id);
    setOwnerDraft(item.owner ?? '');
  };
  const cancelOwnerEditor = (): void => {
    setOwnerEditId(null);
    setOwnerDraft('');
  };
  const saveOwner = (item: QueueItem): void => {
    const owner = ownerDraft.trim() || null;
    patchItem(item.id, { owner });
    logAction('set_anomaly_owner', { item_id: item.id });
    void putAnomalyWorkstate(item.source, item.sourceId, { owner_label: owner }, getSessionId())
      .then(broadcastWorkstate)
      .catch((err) => console.error('owner persist failed', err));
    cancelOwnerEditor();
  };

  // #264 — seat editor / first-run prompt plumbing. Save with an empty draft
  // behaves like Later (close; snooze the nudge for the session if still unnamed).
  const openSeatEditor = (): void => {
    setSeatDraft(seat ?? '');
    setSeatEditorOpen(true);
  };
  const closeSeatEditor = (): void => {
    setSeatEditorOpen(false);
    setSeatDraft('');
    if (!seat) {
      dismissSeatPrompt();
      setSeatPromptDismissed(true);
    }
  };
  const saveSeat = (): void => {
    const trimmed = seatDraft.trim();
    if (!trimmed) {
      closeSeatEditor();
      return;
    }
    setSeat(trimmed);
    setSeatEditorOpen(false);
    setSeatDraft('');
  };

  // #144 — selection toggling + bulk ack over the persisted path.
  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = useMemo(
    () => items.filter((it) => hazardFilter === 'all' || it.hazard === hazardFilter),
    [items, hazardFilter],
  );
  // Acknowledged state is server-persisted on the item (migration 0058, #140) — it
  // survives a reload and is visible to a second operator (the shift-handover proof).
  const isAcked = (it: QueueItem): boolean => it.acknowledged;
  const active = useMemo(() => filtered.filter((it) => !isAcked(it)), [filtered]);
  const acknowledged = filtered.filter(isAcked);

  // #394 de-flood — partition the ACTIVE (unacked) set into the urgent triage surface
  // and the sub-urgent "Fusion candidates" drawer through the ONE shared partition
  // (QF1: list / urgent map / SITREP all gate on RL≥3). Acked rows are untouched — they
  // stay in the collapsed Acknowledged section below, so an acked RL≥3 red is NEVER
  // dropped into the drawer (QF4: de-flood ≠ drop acked reds). The standing-alarm strip
  // and the L-count chips still read the FULL `items` set, so neither changes here.
  const { urgent: activeUrgent, candidates: activeCandidates } = useMemo(
    () => partitionQueue(active),
    [active],
  );

  // ★ Orphan narrowing — orphans are RL≥3, so they already live in activeUrgent; the toggle
  // narrows the shown urgent set to the un-owned ones (owned reds drop out). No change to
  // partitionQueue (the drawer + acked sections are untouched).
  const shownUrgent = useMemo(
    () => (orphansOnly ? activeUrgent.filter(isOrphan) : activeUrgent),
    [activeUrgent, orphansOnly],
  );

  // Urgent rows in render/traversal order (grouped or flat share HAZARD_ORDER) — the
  // single source the #145 j/k navigation walks. The drawer's candidates are a secondary
  // surface (reachable by click), so the keyboard fast-path stays on the urgent bar.
  const orderedActive = useMemo(
    () =>
      groupByHazard
        ? HAZARD_ORDER.flatMap((h) => shownUrgent.filter((it) => it.hazard === h))
        : shownUrgent,
    [shownUrgent, groupByHazard],
  );

  // #147 — rows newer than the shift-start anchor (the "since shift start" set).
  const isSinceShift = (it: QueueItem): boolean => Date.parse(it.timeIso) >= shiftStart;
  const newSinceCount = useMemo(
    () => active.filter((it) => Date.parse(it.timeIso) >= shiftStart).length,
    [active, shiftStart],
  );

  const bulkAck = (): void => {
    const targets = active.filter((it) => selectedIds.has(it.id));
    for (const t of targets) ackItem(t);
    setSelectedIds(new Set());
  };

  const resetShiftStart = (): void => {
    if (sinceFocus) resetDisplayed();
    resetShiftStartAnchor();
  };
  const toggleSinceFocus = (): void => {
    resetDisplayed();
    setSinceFocus((v) => !v);
  };
  const toggleShowAllCanada = (): void => {
    resetDisplayed();
    setSelectedIds(new Set());
    setShowAllCanada((v) => !v);
  };
  // ★ Orphan filter — a pure CLIENT-SIDE render filter (no fetch-param change), so it must NOT
  // resetDisplayed (that empties the fetch-driven set): the shownUrgent memo re-derives from the
  // already-displayed items when orphansOnly flips.
  const toggleOrphansOnly = (): void => setOrphansOnly((v) => !v);

  // #145 — list-level keyboard handler: j/k roving focus, a acks, x selects. Enter
  // (open) is handled on the row itself. Typing in the owner editor is never hijacked.
  const onListKeyDown = (e: React.KeyboardEvent): void => {
    if (ownerEditId) return;
    const order = orderedActive;
    if (order.length === 0) return;
    if (e.key === 'j' || e.key === 'k') {
      e.preventDefault();
      const idx = order.findIndex((it) => it.id === focusedId);
      const next =
        idx === -1
          ? e.key === 'j'
            ? 0
            : order.length - 1
          : e.key === 'j'
            ? Math.min(order.length - 1, idx + 1)
            : Math.max(0, idx - 1);
      const id = order[next]!.id;
      setFocusedId(id);
      rowRefs.current.get(id)?.focus();
    } else if (e.key === 'a' && focusedId) {
      const it = order.find((i) => i.id === focusedId);
      if (it) {
        e.preventDefault();
        ackItem(it);
      }
    } else if (e.key === 'x' && focusedId) {
      e.preventDefault();
      toggleSelect(focusedId);
    }
  };

  const renderRow = (item: QueueItem): JSX.Element => {
    const owner = item.owner;
    const status = item.microStatus;
    const editingOwner = ownerEditId === item.id;
    const isSel = selectedId === item.id;
    const isNew = highlightIds.has(item.id);
    const isFocused = focusedId === item.id;
    const checked = selectedIds.has(item.id);
    const sinceShift = isSinceShift(item);
    return (
      <div
        key={item.id}
        ref={(el) => {
          if (el) rowRefs.current.set(item.id, el);
          else rowRefs.current.delete(item.id);
        }}
        role="button"
        tabIndex={0}
        data-testid="anomaly-queue-row"
        data-source={item.source}
        data-new={isNew ? 'true' : undefined}
        onClick={() => select(item)}
        onFocus={() => setFocusedId(item.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            select(item);
          }
        }}
        className={`relative w-full text-left pl-3 pr-2 py-1.5 hover:bg-slate-800/70 cursor-pointer ${
          isSel ? 'bg-slate-800' : ''
        } ${isFocused ? 'ring-1 ring-inset ring-sky-500' : ''} ${
          isNew ? 'queue-arrival-highlight' : ''
        }`}
      >
        <span
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{ backgroundColor: accentColor(item) }}
        />
        {/* Line 1: select checkbox · icon · title · Threat-level/pending badge (#613 —
            the anomaly queue is the RAW-hazard triage surface → the machine Threat axis). */}
        <div className="flex items-center gap-1.5">
          {!isAcked(item) && (
            <input
              type="checkbox"
              data-testid="anomaly-queue-select"
              checked={checked}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleSelect(item.id)}
              title="Select for bulk acknowledge"
              className="shrink-0 accent-emerald-500"
            />
          )}
          <span className="text-sm leading-none">{item.hazardIcon}</span>
          <span className="flex-1 text-[13px] font-semibold truncate">{item.title}</span>
          {item.source === 'smoke' ? (
            <span
              className="shrink-0 text-[9px] px-1 py-0.5 rounded font-medium"
              style={{ backgroundColor: `${SMOKE_ANOMALY_COLOR}22`, color: SMOKE_ANOMALY_COLOR }}
            >
              pending
            </span>
          ) : item.source === 'media_signal' ? (
            <span
              className="shrink-0 text-[9px] px-1 py-0.5 rounded font-medium"
              style={{
                backgroundColor: `${MEDIA_SIGNAL_ANOMALY_COLOR}22`,
                color: MEDIA_SIGNAL_ANOMALY_COLOR,
              }}
            >
              {MEDIA_SIGNAL_CHIP_LABEL}
            </span>
          ) : (
            <ResponseLevelBadge level={item.responseLevel} axis="threat" />
          )}
        </div>
        {/* Line 2: time · since-shift tag · kicker · corroboration */}
        <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-slate-400 truncate">
          <span>{relTime(item.timeIso)}</span>
          {sinceShift && (
            <span
              data-testid="anomaly-queue-since-tag"
              className="shrink-0 text-[9px] px-1 rounded bg-amber-500/20 text-amber-300"
              title="Arrived since shift start"
            >
              new
            </span>
          )}
          <span>·</span>
          <span className="truncate">{item.kicker}</span>
          {item.corroboration && (
            <>
              <span>·</span>
              <span className="truncate text-slate-500">{item.corroboration}</span>
            </>
          )}
          {/* #447 Track A Slice 3b — the demotion reason for a hotspot suppressed off
              urgent (the known fire IS the alert). Muted amber chip so the operator reads
              WHY this near-a-known-fire hotspot is a quiet L2 card, not a red. */}
          {item.demotionReason && (
            <span
              data-testid="anomaly-queue-demotion-reason"
              className="shrink-0 text-[9px] px-1 rounded bg-amber-500/15 text-amber-300/90 truncate max-w-[160px]"
              title={item.demotionReason}
            >
              {item.demotionReason}
            </span>
          )}
          {/* #448 Track A Slice 3c — the values-at-risk VETO kept this genuine, unconnected
              hotspot urgent because a NATIONAL populated place is within range. Emerald (NOT
              amber) chip so the operator reads it as a KEEP ("near a community → stays red"),
              the inverse of a demotion. The downwind marker flags the evacuation-risk lift. */}
          {item.valuesKeptUrgent && (
            <span
              data-testid="anomaly-queue-values-kept-urgent"
              className="shrink-0 text-[9px] px-1 rounded bg-emerald-500/15 text-emerald-300/90 truncate max-w-[180px]"
              title={item.valuesKeptUrgent}
            >
              {item.valuesKeptUrgent}
            </span>
          )}
        </div>
        {isOwnedByOther(item) ? (
          /* #264 — claimed by another desk: surface the owner, lock the workflow
             chips (read-only), keep the row itself openable. */
          <div className="flex items-center gap-1 mt-1">
            <span
              data-testid="anomaly-queue-owned-chip"
              className="text-[9px] px-1 py-0.5 rounded font-medium truncate max-w-[200px]"
              style={{ backgroundColor: `${ACK_STEEL}22`, color: ACK_STEEL }}
              title={`Claimed by ${item.owner} at first acknowledge — workflow actions live on that desk (honor system · pre-RBAC). Open the row to view read-only.`}
            >
              ACK — Claimed by {item.owner}
            </span>
            <span
              className="text-[9px] px-1 py-0.5 rounded bg-slate-800/60 text-slate-500"
              title="Status (read-only — claimed by another seat)"
            >
              {MICRO_STATUS_LABEL[status]}
            </span>
          </div>
        ) : (
        <div className="flex items-center gap-1 mt-1">
          <span
            role="button"
            tabIndex={0}
            {...activate(() => cycleStatus(item))}
            className="text-[9px] px-1 py-0.5 rounded bg-slate-700/70 text-slate-300 hover:bg-slate-600"
            title="Advance status: New → In review → Cleared"
          >
            {MICRO_STATUS_LABEL[status]}
          </span>
          {editingOwner ? (
            <input
              autoFocus
              value={ownerDraft}
              data-testid="anomaly-queue-owner-input"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setOwnerDraft(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') saveOwner(item);
                else if (e.key === 'Escape') cancelOwnerEditor();
              }}
              onBlur={() => saveOwner(item)}
              placeholder="assignee / position"
              className="text-[10px] px-1 py-0.5 rounded bg-slate-900 border border-slate-600 text-slate-100 w-[120px] outline-none focus:border-sky-500"
            />
          ) : (
            <span
              role="button"
              tabIndex={0}
              data-testid="anomaly-queue-owner-chip"
              {...activate(() => openOwnerEditor(item))}
              className="text-[9px] px-1 py-0.5 rounded bg-slate-700/70 text-slate-300 hover:bg-slate-600 truncate max-w-[120px]"
              title="Set assignee / position"
            >
              {owner ? `👤 ${owner}` : 'assign'}
            </span>
          )}
          {!isAcked(item) && (
            <span
              role="button"
              tabIndex={0}
              data-testid="anomaly-queue-ack"
              {...activate(() => ackItem(item))}
              className="ml-auto text-[9px] px-1 py-0.5 rounded bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50"
              title={seat ? `Acknowledge & claim for ${seat} (first ack claims)` : 'Acknowledge (demotes; marker stays)'}
            >
              ✓ Ack
            </span>
          )}
        </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="anomaly-queue-panel">
      {!visible ? (
        <div className="px-3 py-4 text-xs text-slate-400">
          The Urgent Alerts layer is off. Enable it in Layers to surface fused
          hotspots, public alerts, and smoke detections.
        </div>
      ) : (
        <>
          {/* Count chips. #325 — the census is UNKNOWN, not zero, while the API is
              unreachable: pass null so each chip reads "—", consistent with the rail
              badge + the "Alerts unavailable" empty-state below (never a row of
              unverifiable "0"s that reads as a quiet all-clear). */}
          <div className="flex items-center gap-2 px-3 py-2 text-[11px] border-b border-slate-800">
            <ChipCount label="L4" n={reachable ? counts.l4 : null} color={RESPONSE_LEVEL_COLORS[4]} />
            <ChipCount label="L3" n={reachable ? counts.l3 : null} color={RESPONSE_LEVEL_COLORS[3]} />
            <ChipCount label="L2" n={reachable ? counts.l2 : null} color={RESPONSE_LEVEL_COLORS[2]} />
            <ChipCount label="L1" n={reachable ? counts.l1 : null} color={RESPONSE_LEVEL_COLORS[1]} />
            <ChipCount label="💨" n={reachable ? counts.smoke : null} color={SMOKE_ANOMALY_COLOR} />
            <ChipCount label="📡" n={reachable ? counts.media : null} color={MEDIA_SIGNAL_ANOMALY_COLOR} />
            {/* ★ Orphan filter (feat/ownership-coordination) — a clickable count chip toggling the
                view to UN-OWNED hazards at Threat Level ≥3 (the hazard nobody has assumed Incident
                Command of — the PEOC's #1 ask). Threat-axis wording; never "Response Level" (#613). */}
            <button
              type="button"
              data-testid="orphan-chip"
              aria-pressed={orphansOnly}
              onClick={toggleOrphansOnly}
              title="Show only un-owned hazards at Threat Level 3+ — the hazard nobody has assumed Incident Command of"
              className={`shrink-0 rounded px-1.5 py-0.5 font-medium ${
                orphansOnly
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
              }`}
            >
              ⚠ Orphan {reachable ? orphanCount : '—'}
            </button>
            {/* #264 — this desk's seat name; first ack claims under it. */}
            <span
              role="button"
              tabIndex={0}
              data-testid="seat-chip"
              {...activate(openSeatEditor)}
              className={`ml-auto shrink-0 px-1.5 py-0.5 rounded truncate max-w-[110px] ${
                seat
                  ? 'bg-slate-700/60 text-slate-300 hover:bg-slate-600'
                  : 'bg-sky-900/60 text-sky-200 hover:bg-sky-800/60'
              }`}
              title="This desk's seat name — the first acknowledge claims an alert under it (honor system · pre-RBAC)"
            >
              {seat ? `⌑ ${seat}` : 'set seat'}
            </span>
            <button
              type="button"
              onClick={() => setStripOpen((v) => !v)}
              className="text-slate-400 hover:text-slate-200"
              title="Sort & filter"
            >
              {stripOpen ? '▴' : '▾'} filter
            </button>
          </div>

          {/* #264 — seat editor / first-run prompt: nudges an unnamed desk once
              per session ("Later" snoozes via sessionStorage); always reachable
              from the seat chip. Honor-system, pre-RBAC (#190). */}
          {(seatEditorOpen || (!seat && !seatPromptDismissed)) && (
            <div
              data-testid="seat-editor"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] border-b border-slate-800 bg-sky-900/20"
              title="Used when claiming alerts: the first desk to acknowledge owns the alert under this name. Honor system — no login (upgrades to RBAC, #190)."
            >
              <span className="shrink-0 text-slate-300">Seat name</span>
              <input
                autoFocus={seatEditorOpen}
                data-testid="seat-name-input"
                value={seatDraft}
                maxLength={SEAT_NAME_MAX}
                onChange={(e) => setSeatDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveSeat();
                  else if (e.key === 'Escape') closeSeatEditor();
                }}
                placeholder="e.g. Desk 2 / Duty Officer"
                className="flex-1 min-w-0 px-1 py-0.5 rounded bg-slate-900 border border-slate-600 text-slate-100 outline-none focus:border-sky-500"
              />
              <button
                type="button"
                data-testid="seat-name-save"
                onClick={saveSeat}
                className="px-1.5 py-0.5 rounded bg-sky-600/70 text-white hover:bg-sky-600"
              >
                Save
              </button>
              <button
                type="button"
                data-testid="seat-name-later"
                onClick={closeSeatEditor}
                className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300 hover:bg-slate-600"
              >
                {seat ? 'Cancel' : 'Later'}
              </button>
            </div>
          )}

          {/* Standing-alarm ceiling (#129, design-council Part II rule 5): a wall of
              permanent red must never normalize. Shows when unacked reds exceed the
              EEMUA console ceiling or the oldest stands longer than a shift. */}
          {standingAlarm && (
            <div
              data-testid="anomaly-queue-standing-alarm"
              role="status"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold border-b border-red-800/60 text-red-200 bg-red-900/40"
            >
              <span aria-hidden>⚠</span>
              <span>
                {standingAlarm.count} unacknowledged · oldest {standingAlarm.oldestHours}h — triage
                mode
              </span>
            </div>
          )}

          {/* #146 — bbox-decouple chip. The triage queue must never silently hide a
              red outside the current map frame. */}
          {showAllCanada ? (
            <button
              type="button"
              data-testid="anomaly-queue-canada-toggle"
              onClick={toggleShowAllCanada}
              className="flex items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-medium text-sky-200 bg-sky-900/60 hover:bg-sky-800/60 border-b border-sky-800"
              title="Return the queue to the current map view"
            >
              <span>🍁 Showing all Canada</span>
              <span className="ml-auto text-sky-300/80">← back to map view</span>
            </button>
          ) : (
            outsideCount > 0 && (
              <button
                type="button"
                data-testid="anomaly-queue-outside-chip"
                onClick={toggleShowAllCanada}
                className="flex items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-medium text-amber-200 bg-amber-900/40 hover:bg-amber-800/50 border-b border-amber-800/70"
                title="These reds are outside the current map frame"
              >
                <span>▲ {outsideCount} outside view</span>
                <span className="ml-auto text-amber-300/90">Show all Canada</span>
              </button>
            )
          )}

          {/* Active class-level mutes (#133) — visible so a shelved class survives
              a shift handover. */}
          {mutedClasses && mutedClasses.length > 0 && (
            <div
              data-testid="anomaly-queue-muted-strip"
              className="flex flex-wrap items-center gap-1.5 px-3 py-1.5 border-b border-slate-800 bg-amber-500/5"
            >
              {mutedClasses.map((name) => (
                <MutedChip key={name} label={`${name} muted`} />
              ))}
            </div>
          )}

          {/* #272 — wall risk-tour desk-clear strip (PART XI Ruling 2.6): the
              wall is read-only, so the tour cards' Clear control lives HERE,
              in the queue panel (the tour domains' triage home). Renders
              nothing while every stop is quiet. */}
          <WallTourDeskStrip />

          {/* #147 — shift-start strip: count of arrivals since the operator came on
              shift + an opt-in "only new" view (passes since= to the fetches). */}
          <div
            data-testid="anomaly-queue-shift-strip"
            className="flex items-center gap-2 px-3 py-1 text-[10px] text-slate-400 border-b border-slate-800"
          >
            <span>
              🕑 Since {clockLabel(shiftStart)} ·{' '}
              <span className="text-slate-200 font-medium">{newSinceCount} new</span>
            </span>
            <button
              type="button"
              data-testid="anomaly-queue-since-toggle"
              onClick={toggleSinceFocus}
              className={`ml-auto px-1.5 py-0.5 rounded ${
                sinceFocus ? 'bg-sky-600 text-white' : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600'
              }`}
              title="Show only anomalies that arrived since shift start"
            >
              {sinceFocus ? 'showing new only' : 'only new'}
            </button>
            <button
              type="button"
              onClick={resetShiftStart}
              className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300 hover:bg-slate-600"
              title="Reset the shift-start marker to now"
            >
              reset
            </button>
          </div>

          {/* Collapsible sort/filter strip (default closed, §9.4) */}
          {stripOpen && (
            <div className="px-3 py-2 border-b border-slate-800 space-y-1.5 text-[11px]">
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Hazard:</span>
                {(['all', 'fire', 'weather', 'smoke', 'ice_storm', 'flood', 'media_signal'] as const).map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHazardFilter(h)}
                    className={`px-1.5 py-0.5 rounded ${
                      hazardFilter === h ? 'bg-sky-600 text-white' : 'bg-slate-700/60 text-slate-300'
                    }`}
                  >
                    {hazardFilterLabel(h)}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-1.5 text-slate-400">
                <input
                  type="checkbox"
                  checked={groupByHazard}
                  onChange={(e) => setGroupByHazard(e.target.checked)}
                />
                Group by hazard (default: flat by level)
              </label>
            </div>
          )}

          {/* #144 — bulk-ack bar (only while a selection exists). */}
          {selectedIds.size > 0 && (
            <div
              data-testid="anomaly-queue-bulk-bar"
              className="flex items-center gap-2 px-3 py-1.5 text-[11px] border-b border-slate-800 bg-emerald-900/30"
            >
              <span className="text-emerald-200 font-medium">{selectedIds.size} selected</span>
              <button
                type="button"
                data-testid="anomaly-queue-bulk-ack"
                onClick={bulkAck}
                className="ml-auto px-2 py-0.5 rounded bg-emerald-600/40 text-emerald-200 hover:bg-emerald-600/60"
              >
                ✓ Ack selected
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="px-2 py-0.5 rounded bg-slate-700/60 text-slate-300 hover:bg-slate-600"
              >
                Clear
              </button>
            </div>
          )}

          {/* Active rows. Pointer-over freezes ordering: a buffered refetch is
              applied only on pointer-out, so a row never moves under the cursor.
              j/k/a/x keyboard nav (#145) is handled at this container. */}
          <div
            className="flex-1 overflow-y-auto"
            onKeyDown={onListKeyDown}
            onMouseEnter={() => {
              interactingRef.current = true;
            }}
            onMouseLeave={() => {
              interactingRef.current = false;
              if (bufferedRef.current) {
                applyIncoming(bufferedRef.current);
                bufferedRef.current = null;
              }
            }}
          >
            {/* "N new ↑" header — parks new arrivals until the operator merges them. */}
            {pendingNew.length > 0 && (
              <button
                type="button"
                data-testid="anomaly-queue-new-header"
                onClick={mergePending}
                className="sticky top-0 z-10 w-full px-3 py-1.5 text-left text-[11px] font-semibold text-sky-200 bg-sky-900/80 hover:bg-sky-800/80 border-b border-sky-700/60"
                title="Merge new arrivals into the list"
              >
                ↑ {pendingNew.length} new — click to show
              </button>
            )}
            {shownUrgent.length === 0 ? (
              // #325 — honest empty-state: an EMPTY list plus an unreachable API is NOT
              // an all-clear. Say "cannot reach live data" (in the same fault register
              // as the banner) instead of the false "No urgent alerts in view."
              <div
                className={
                  reachable ? 'px-3 py-4 text-xs text-slate-500' : 'px-3 py-4 text-xs text-red-300'
                }
                data-testid="anomaly-queue-empty"
              >
                {!reachable
                  ? ALERTS_UNAVAILABLE_EMPTY
                  : orphansOnly
                    ? 'No un-owned hazards at Threat Level 3+ — every urgent hazard has a command lead.'
                    : sinceFocus
                      ? 'No urgent alerts since shift start.'
                      : 'No urgent alerts in view.'}
              </div>
            ) : groupByHazard ? (
              HAZARD_ORDER.map((h) => {
                const group = shownUrgent.filter((it) => it.hazard === h);
                if (group.length === 0) return null;
                return (
                  <div key={h}>
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/60">
                      {hazardFilterLabel(h)} · {group.length}
                    </div>
                    {group.map(renderRow)}
                  </div>
                );
              })
            ) : (
              shownUrgent.map(renderRow)
            )}

            {/* #394 de-flood — "Fusion candidates · N" drawer: the #370-demoted sub-urgent
                (Threat Level 1–2, #613) SCORED mass that used to flood the urgent bar. Default
                collapsed, one click away, source-neutral (holds hotspot / flood / ice
                candidates, not "sub-urgent hotspots"). The MAP already renders these
                (FusionCandidatesLayer) — they are demoted off the urgent surface here,
                never silently dropped (QF3). */}
            {activeCandidates.length > 0 && (
              <div className="border-t border-slate-800 mt-1">
                <button
                  type="button"
                  data-testid="anomaly-queue-candidates-toggle"
                  onClick={() => setCandidatesOpen((v) => !v)}
                  className="w-full px-3 py-1.5 text-left text-[11px] text-slate-400 hover:text-slate-200"
                  title="Sub-urgent fused candidates (Threat Level 1–2) — below the urgent bar; the map shows them too"
                >
                  {candidatesOpen ? '▾' : '▸'} Fusion candidates · {activeCandidates.length}
                </button>
                {candidatesOpen && (
                  <div data-testid="anomaly-queue-candidates" className="opacity-80">
                    {activeCandidates.map(renderRow)}
                  </div>
                )}
              </div>
            )}

            {/* Acknowledged (collapsed, audit-safe) */}
            {acknowledged.length > 0 && (
              <div className="border-t border-slate-800 mt-1">
                <button
                  type="button"
                  onClick={() => setAckOpen((v) => !v)}
                  className="w-full px-3 py-1.5 text-left text-[11px] text-slate-400 hover:text-slate-200"
                >
                  {ackOpen ? '▾' : '▸'} Acknowledged · {acknowledged.length}
                </button>
                {ackOpen && <div className="opacity-60">{acknowledged.map(renderRow)}</div>}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ChipCount({
  label,
  n,
  color,
}: {
  label: string;
  /** #325 — `null` = UNKNOWN (API unreachable): the chip shows "—", never a false "0". */
  n: number | null;
  color: string;
}): JSX.Element {
  const display = n === null ? '—' : n;
  return (
    <span className="inline-flex items-center gap-1" title={`${label}: ${display}`}>
      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-slate-300">
        {label} {display}
      </span>
    </span>
  );
}
